<template>
  <Modal :open="props.open" @close-modal="$emit('close-modal')">
    <template #modal-header>
      <div>
        <div class="flex items-center">
          <Icon name="Plus" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Add</div>
        </div>
        <div class="font-league tracking-[-.56px] font-semibold text-[1.75rem] text-strongest">
          Add Beneficial Owner
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-2 lg:p-6">
        <div v-if="adding_new">
          <TabView
            :tabs="props.modal_config.tabs"
            :activeTab="activeProfileTab"
            @update:activeTab="(key) => (activeProfileTab = key)"
          >
            <template
              v-for="subtab in props.modal_config.tabs"
              :key="subtab.slot_name"
              #[subtab.slot_name]
            >
              <div v-if="props.modal_config.groups?.includes(subtab.label)">
                <Form
                  :schema_array="
                    props.modal_config.schema.filter(
                      (item) => item.record_detail_group === subtab.label
                    )
                  "
                  :state_object="getStateObject(subtab.label)"
                  :record_id="props.modal_config.record_id"
                  variant="secondary"
                />
              </div>
            </template>
          </TabView>
        </div>
        <div v-else>
          <div class="font-medium font-league text-2xl tracking-[-0.48] text-strongest mb-4">
            List of available beneficial owners
          </div>
          <div v-if="beneficialOwners.length" class="space-y-2">
            <AsteriskNotice
              v-if="show_missing_selection"
              variant="error"
              size="medium"
              text="Please select a beneficial owner to continue"
              class="mb-4"
            />
            <div
              v-for="ben_owner in beneficialOwners"
              :key="ben_owner.record_id"
              @click="selectedContactId = ben_owner.record_id"
              class="flex items-center py-5 px-3 bg-container-bg rounded-xl cursor-pointer transition-all duration-200 border-2"
              :class="[
                selectedContactId === ben_owner.record_id
                  ? ' border-radio-checkbox-outline'
                  : ' border-transparent hover:border-radio-checkbox-selector',
              ]"
            >
              <!-- Circle -->
              <div
                class="w-6 h-6 mr-5 rounded-full flex items-center justify-center border-2 transition-all"
                :class="[
                  selectedContactId === ben_owner.record_id
                    ? 'border-radio-checkbox-checked bg-radio-checkbox-checked'
                    : 'border-radio-checkbox-outline',
                ]"
              >
                <div
                  v-if="selectedContactId === ben_owner.record_id"
                  class="w-[0.875rem] h-[0.875rem] rounded-full bg-container-bg"
                />
              </div>

              <!-- Name -->
              <div
                class="text-base text-strongest font-inter font-medium"
                :class="selectedContactId == ben_owner.record_id ? 'font-semibold' : ''"
              >
                {{ ben_owner.first_name }} {{ ben_owner.last_name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="flex justify-between gap-2 items-center">
        <div>
          <Button @click="adding_new = true" v-if="!adding_new" variant="paper-primary" size="sm">
            <template #icon>
              <Icon name="Plus" class="h-4 w-4" />
            </template>
            <template #label>New</template>
          </Button>
        </div>
        <div class="flex gap-2">
          <Button @click="$emit('close-modal')" variant="paper-primary" size="sm" class="">
            <template #label>Cancel</template>
          </Button>
          <Button @click="handleConfirm()" variant="primary-grey" size="sm" class="">
            <template #label>Confirm</template>
          </Button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
//IMPORTS
import { ref, watch, computed } from "vue";
import { Modal, TabView, Form, Icon, AsteriskNotice, Button } from "@/components";
import { useCrudStore, useSchemaStore, useDataStore, useUIStore } from "@/stores";
import _ from "lodash";

//STORES
const crudStore = useCrudStore();
const dataStore = useDataStore();
const schemaStore = useSchemaStore();
const UIStore = useUIStore();

//EMITS & PROPS
const emit = defineEmits(["close-modal", "confirm"]);
const props = defineProps({
  open: { type: Boolean, required: true },
  modal_config: { type: Object, required: true },
});

//REFS
const adding_new = ref(false);
const activeProfileTab = ref("personal.personal_information");
const show_missing_selection = ref(false);
const selectedContactId = ref(null);

//WATCHERS
watch(
  () => selectedContactId.value,
  (new_object) => {
    if (new_object) {
      show_missing_selection.value = false;
    }
  },
  { immediate: true }
);

watch(
  () => props.modal_config.open,
  (is_open) => {
    if (is_open) {
      adding_new.value = false;
      activeProfileTab.value = props.modal_config.tabs[0].slot_name;
    } else {
      schemaStore.show_input_errors = false;
    }
  },
  { immediate: true }
);

//COMPONENT LOGIC AND FUNCTIONS
function getStateObject(group) {
  if (group == "ID Information") {
    return props.modal_config.object_to_edit.id_info
      ? props.modal_config.object_to_edit.id_info
      : {};
  }
  return props.modal_config.object_to_edit;
}

const beneficialOwners = computed(() => {
  let ben_owners = JSON.parse(JSON.stringify(dataStore.beneficial_owner_contacts));
  ben_owners.push(dataStore.contact);

  ben_owners = ben_owners.filter((ben_owner) => {
    if (ben_owner.entity_ids) {
      return !ben_owner.entity_ids.includes(props.modal_config.entity_id);
    }
    return true;
  });

  return ben_owners;
});

async function handleConfirm() {
  try {
    if (adding_new.value) {
      let object = props.modal_config.object_to_edit;

      let _continue = schemaStore.validateFields(
        object,
        props.modal_config.schema,
        null,
        true,
        "beneficial_owner"
      );

      if (_continue.proceed) {
        object.user_id = dataStore.contact.user_id;
        object = schemaStore.addCreatedUpdatedFields(object, true, true);
        object.entity_ids = [props.modal_config.entity_id];

        let insert_res = await crudStore.insert("contacts", [object]);

        if (insert_res.status !== 200) {
          throw new Error("Failed to create beneficial owner contact");
        }

        await handleActivityLog(true, null, true);
      } else {
        let invalid_tab = _continue.invalid_groups[0]
          ? props.modal_config.tabs.find((tab) => tab.label === _continue.invalid_groups[0])
          : props.modal_config.tabs[0];

        activeProfileTab.value = invalid_tab.slot_name;

        await handleActivityLog(false, _continue, true);
        return;
      }
    } else {
      if (!selectedContactId.value) {
        show_missing_selection.value = true;
        return;
      }

      // find the full contact from beneficialOwners
      let selectedContact = beneficialOwners.value.find(
        (contact) => contact.record_id === selectedContactId.value
      );

      if (selectedContact) {
        // this is where we would link the existing beneficial owner contact to the entity

        let query = [
          {
            field_name: "record_id",
            operator: "eq",
            value: selectedContactId.value,
          },
        ];

        let updated_entity_ids = selectedContact.entity_ids ? [...selectedContact.entity_ids] : [];
        updated_entity_ids.push(props.modal_config.entity_id);

        let update = {
          entity_ids: updated_entity_ids,
        };

        update = schemaStore.addCreatedUpdatedFields(update, false, true);

        let update_res = await crudStore.update("contacts", query, update);

        if (update_res.status !== 200) {
          throw new Error("Failed to link beneficial owner to entity");
        }

        await handleActivityLog(true, null, false);
      }
    }

    emit("confirm", {
      type: "beneficial_owner",
      record_id: props.modal_config.record_id,
    });

    emit("close-modal");
  } catch (e) {
    var alert = {
      type: "error",
      message: `Failed to add beneficial owner to entity.`,
    };
    UIStore.animateNotificationAlert(alert);
    emit("close-modal");
  }
}

async function handleActivityLog(valid, validation_details, is_new = adding_new.value) {
  let status = valid ? "Completed" : "Failed Validation";

  let log = {
    step: "Profile Page",
    substep: "Add Beneficial Owner to Entity",
    status: status,
    message: is_new
      ? "Added new beneficial owner to entity"
      : "Added existing beneficial owner to entity",
    url: window.location.href,
    validation_details: valid ? null : JSON.stringify(validation_details),
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  await crudStore.insert("investoractivity", [log]);
}
</script>
